import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import StructuredData from '@/components/feature/StructuredData';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const canonicalUrl = typeof window !== 'undefined' ? `${window.location.origin}/about` : '';

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - SM专业指南"
        description="SM专业指南的品牌介绍、团队声誉与联系方式。我们致力于提供专业、安全、负责任的SM知识科普，帮助每一位探索者建立正确的认知框架。"
        keywords="SM专业指南,关于我们,品牌介绍,SM知识科普,联系我们"
        canonicalUrl={canonicalUrl}
      />
      <StructuredData
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: canonicalUrl.replace('/about', '') },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: canonicalUrl },
          ],
        }}
      />
      <StructuredData
        type="WebPage"
        data={{
          name: '关于我们 - SM专业指南',
          description: 'SM专业指南的品牌介绍、团队声誉与联系方式',
          url: canonicalUrl,
        }}
      />
      <StructuredData
        type="Organization"
        data={{
          name: 'SM专业指南',
          url: canonicalUrl,
          description: '致力于提供专业、安全、负责任的SM知识科普，帮助每一位探索者建立正确的认知框架。',
          sameAs: [canonicalUrl],
        }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
            <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />

            <div className="mt-6 max-w-3xl">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                关于SM专业指南
              </h1>
              <p className="text-foreground-600 text-base md:text-lg mt-4 leading-relaxed">
                SM专业指南是一个致力于提供专业、安全、负责任的SM知识科普平台。我们的使命是帮助对SM文化感兴趣的用户建立正确的认知框架，在安全、理智和知情同意的前提下负责任地探索。
              </p>
            </div>
          </div>
        </div>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100/50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-xs px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                  品牌故事
                </span>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mt-4 mb-4">
                  从知识空白到专业指南
                </h2>
                <div className="text-foreground-600 text-sm leading-relaxed space-y-4">
                  <p>
                    SM专业指南的创立源于一个简单而深刻的观察：在中文互联网上，关于SM的知识资源要么过于零散碎片化，要么充斥着错误信息和偏见。对于真正想要学习、理解和负责任地探索SM文化的用户来说，缺乏一个系统、专业、安全的知识入口。
                  </p>
                  <p>
                    我们的编辑团队由具有心理学、社会学和性教育背景的专业人士组成。我们坚信，知识是最好的安全保障——只有在充分了解的基础上，人们才能做出真正知情的选择。每一篇文章都经过严格的审核流程，确保内容的准确性、科学性和安全性。
                  </p>
                  <p>
                    自创立以来，我们已经帮助数以万计的读者建立了对SM文化的正确认知。我们深知，SM不仅仅是关于身体实践，更是关于信任、沟通、边界和人与人之间的深层连接。
                  </p>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20creative%20team%20workspace%20with%20abstract%20educational%20elements%2C%20soft%20amber%20and%20cream%20tones%2C%20books%20and%20knowledge%20symbols%2C%20modern%20clean%20aesthetic%2C%20natural%20lighting%2C%20editorial%20quality%2C%20hopeful%20atmosphere%2C%20team%20collaboration%20concept&width=800&height=600&seq=about-brand-story&orientation=landscape"
                  alt="SM专业指南品牌故事"
                  title="SM专业指南 - 品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                核心价值
              </span>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mt-4">
                我们的承诺
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '安全第一',
                  desc: '所有内容以安全准则为核心，确保读者在安全框架内探索',
                  color: 'bg-accent-100 text-accent-500',
                },
                {
                  icon: 'ri-book-read-line',
                  title: '专业严谨',
                  desc: '由专业人士编写审核，确保信息的准确性与科学性',
                  color: 'bg-primary-100 text-primary-500',
                },
                {
                  icon: 'ri-user-heart-line',
                  title: '以人为本',
                  desc: '尊重每一位读者的个人边界和探索节奏，反对任何形式的强迫',
                  color: 'bg-secondary-100 text-secondary-500',
                },
                {
                  icon: 'ri-global-line',
                  title: '开放包容',
                  desc: '以开放的态度呈现多元视角，不预设任何道德评判立场',
                  color: 'bg-accent-100 text-accent-500',
                },
              ].map((val, i) => (
                <div key={i} className="text-center p-6 bg-background-50 rounded-xl border border-background-200/70">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-lg ${val.color} mx-auto mb-4`}>
                    <i className={`${val.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{val.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100/50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20team%20reputation%20and%20trust%20concept%20art%2C%20warm%20amber%20and%20cream%20tones%2C%20abstract%20geometric%20shapes%20representing%20credibility%2C%20clean%20modern%20aesthetic%2C%20soft%20natural%20lighting%2C%20editorial%20quality%2C%20trustworthy%20atmosphere&width=800&height=600&seq=about-reputation&orientation=landscape"
                  alt="SM专业指南声誉"
                  title="SM专业指南 - 行业声誉"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2">
                <span className="text-xs px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 font-medium">
                  行业声誉
                </span>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mt-4 mb-4">
                  值得信赖的知识来源
                </h2>
                <div className="text-foreground-600 text-sm leading-relaxed space-y-4">
                  <p>
                    SM专业指南在中文BDSM教育领域建立了良好的声誉。我们的内容被多个相关社群推荐为入门学习资源，也被多位性教育领域的专业人士引用和推荐。
                  </p>
                  <p>
                    我们始终坚持非商业化的运营模式，不接受任何可能影响内容客观性的商业赞助。网站上的所有内容均免费向公众开放，因为我们相信，安全知识不应该有任何门槛。
                  </p>
                  <p>
                    我们与多位心理学、性学领域的学者保持持续的合作关系，定期邀请专业人士参与内容的审核和更新，确保我们的知识库始终与最新的学术研究保持同步。
                  </p>
                </div>

                <div className="mt-8 grid grid-cols-3 gap-4">
                  {[
                    { num: '50万+', label: '累计阅读量' },
                    { num: '10+', label: '专业作者' },
                    { num: '98%', label: '好评率' },
                  ].map((stat, i) => (
                    <div key={i} className="text-center p-4 bg-background-50 rounded-lg border border-background-200/70">
                      <div className="text-xl md:text-2xl font-bold text-primary-500">{stat.num}</div>
                      <div className="text-xs text-foreground-600 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-xs px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                联系我们
              </span>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mt-4 mb-3">
                期待您的反馈
              </h2>
              <p className="text-foreground-600 text-sm leading-relaxed">
                如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
              {[
                {
                  icon: 'ri-mail-line',
                  title: '电子邮箱',
                  content: 'contact@smguide.example.com',
                },
                {
                  icon: 'ri-message-2-line',
                  title: '内容反馈',
                  content: 'feedback@smguide.example.com',
                },
                {
                  icon: 'ri-shake-hands-line',
                  title: '合作咨询',
                  content: 'partner@smguide.example.com',
                },
              ].map((contact, i) => (
                <div
                  key={i}
                  className="text-center p-6 bg-background-50 rounded-xl border border-background-200/70"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 mx-auto mb-3">
                    <i className={`${contact.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">{contact.title}</h3>
                  <p className="text-xs text-foreground-600">{contact.content}</p>
                </div>
              ))}
            </div>

            <ContactForm />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value || '';
    if (honeypot.trim()) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const searchParams = new URLSearchParams();
      formData.forEach((value, key) => {
        searchParams.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9u4kf5ca23r0tr1aqfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: searchParams.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (
        response.ok &&
        parsed &&
        parsed.code === 'OK'
      ) {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8" data-readdy-form="">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="contact-name">
            姓名
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
            placeholder="您的姓名"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="contact-email">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
            placeholder="your@email.com"
          />
        </div>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="contact-subject">
          主题
        </label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          required
          className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="消息主题"
        />
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="contact-message">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
          placeholder="请输入您的留言内容（不超过500字符）"
        ></textarea>
        <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
      </div>

      <div className="phone-alt-wrapper" style={{ position: 'absolute', left: '-9999px', opacity: 0 }}>
        <input
          type="text"
          name="phone_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <button
        type="submit"
        className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
      >
        发送留言
      </button>

      {formStatus === 'success' && (
        <div className="mt-4 p-4 bg-accent-100/60 rounded-lg text-sm text-accent-800 text-center">
          感谢您的留言！我们会尽快回复您。
        </div>
      )}
      {formStatus === 'error' && (
        <div className="mt-4 p-4 bg-red-50 rounded-lg text-sm text-red-700 text-center">
          {formError}
        </div>
      )}
    </form>
  );
}